// Growth Systems - job cards (step-by-step instructions), keyed by job code. (G cards and F1)
// A step is { text, tag }; a red STOP bar is { stop }. Loaded automatically by constants/jobCards.js.

export const division = "Growth Systems";

export const cards = {
  G1: {
    title: "Blog post",
    before: [
      "The topic is on an approved month plan",
      "Brief with the keyword and what the reader should do",
      "Client word list",
    ],
    steps: [
      { text: "Read the brief: keyword, who it is for, what they should do after reading", tag: "Brief clear" },
      { text: "Search the keyword yourself and read the top results. What is missing from them?", tag: "Research notes" },
      { text: "Write the outline: headings, the short answer at the top, the FAQs", tag: "Outline" },
      { text: "Check the outline against the brief before you write a word", tag: "Outline checked" },
      { text: "Write the full draft", tag: "Draft" },
      { text: "Make the images yourself, in the same session: main image and any in-content ones", tag: "Images" },
      { text: "EVERY number or claim must link to where it came from. Not to an article that quotes it", tag: "Sources linked" },
      { text: "If you cannot find the source, REMOVE the claim. Do not soften it", tag: "Clean draft" },
      { text: "Check names and terms against the client word list", tag: "Terms correct" },
      { text: "Self check: spelling, headings in order, links work, meta, CTA, mobile reading", tag: "Self check" },
      { text: "Submit the WHOLE package together: draft, images, meta. Not the text now and images later", tag: "Package" },
      { stop: "Division Lead or named project lead approves" },
      { text: "Publish and set it up properly: title, meta, slug, images, alt text", tag: "Published" },
      { text: "Check it live: is it there, indexed, links working, images showing", tag: "Live checked" },
      { text: "Add links to it from at least TWO older pages. Without this it stays invisible", tag: "Links added" },
      { text: "Note a check at 30 days and 90 days", tag: "Checks booked" },
    ],
    finishedWhen: "Live, indexed, linked from two older pages, and the 30 and 90 day checks are booked.",
  },
  G2: {
    title: "SEO audit",
    before: [
      "All SEO access granted and working",
      "Target locations and competitors confirmed",
    ],
    steps: [
      { text: "Check every access works. If something is blocked, the clock stops", tag: "Access ok" },
      { text: "Pull the tool reports and the search console data", tag: "Data" },
      { text: "Check the technical basics: speed, broken links, redirects, duplicate pages", tag: "Tech issues" },
      { text: "Check the pages: titles, descriptions, headings, do they match what people search", tag: "On-page issues" },
      { text: "Check if two of their own pages are competing for the same search", tag: "Overlap found" },
      { text: "Find pages that used to get traffic and no longer do", tag: "Decay list" },
      { text: "Check the links pointing at their site", tag: "Link profile" },
      { text: "Test a FIXED set of questions in AI answers and write down the set", tag: "AI baseline" },
      { text: "Sort everything by how much it matters and how hard it is", tag: "Priority list" },
      { stop: "Project lead (if named), then Division Lead, then founders check" },
      { stop: "Client agrees the priorities on a call. An audit that is emailed gets skimmed" },
    ],
    finishedWhen: "Report delivered, priorities agreed, and the AI question set saved so next month compares.",
  },
  G3: {
    title: "Content plan for the month",
    before: [
      "Approved SEO strategy",
      "Last month's numbers",
    ],
    steps: [
      { text: "Take the topics from the approved strategy", tag: "Topics" },
      { text: "Look at what did well and what did not last month", tag: "Learnings" },
      { text: "Pick this month's titles. For each: keyword, who it is for, why we are writing it", tag: "Title list" },
      { text: "Include some refreshes of old pages, not only new ones", tag: "Mix" },
      { text: "Put a writer on each one with four dates: brief, draft, review, publish", tag: "Assigned" },
      { text: "Check the team actually has the hours", tag: "Capacity ok" },
      { stop: "Division Lead or named project lead approves, then share with the client" },
    ],
    finishedWhen: "Month planned, every item has a name and four dates, and the client has seen it.",
  },
  G4: {
    title: "LinkedIn post copy",
    before: [
      "The slot is on the calendar the social team keeps",
      "Approved LinkedIn strategy and voice notes",
    ],
    steps: [
      { text: "Take the theme and format from the approved strategy", tag: "Brief" },
      { text: "Write it. The FIRST TWO LINES decide everything — LinkedIn hides the rest until someone clicks", tag: "Draft" },
      { text: "Read it back: is the hook strong, is it easy to scan, does it sound like the client", tag: "Self check" },
      { text: "Check every claim and number. This publishes under a real person's name", tag: "Facts checked" },
      { text: "If it needs a design, tell the social team THREE working days ahead", tag: "Design raised" },
      { stop: "Division Lead or named project lead approves" },
      { text: "Send the copy to the social team for their calendar slot", tag: "Delivered" },
      { text: "When it goes live, reply to the early comments yourself. The first hour decides the reach", tag: "First hour done" },
    ],
    finishedWhen: "Copy delivered on time, and the first hour after publishing was worked.",
  },
  G5: {
    title: "Set up cold email sending",
    before: [
      "Approved outbound plan and copy",
      "Permission to buy domains",
    ],
    steps: [
      { text: "Buy separate domains. NEVER send cold email from the client's main domain", tag: "Domains" },
      { text: "Before buying, agree in writing who owns the domains when the contract ends", tag: "Ownership agreed" },
      { text: "Make 3 inboxes per domain, using Google or Microsoft only", tag: "Inboxes" },
      { text: "Set the three email settings that prove the mail is really from us", tag: "Records set" },
      { text: "Point the domains at the client's real website", tag: "Redirects" },
      { text: "Start warming up. This takes 2 to 3 WEEKS and cannot be rushed", tag: "Warming" },
      { text: "Keep warming even after you start sending for real", tag: "Still warming" },
      { text: "Set how many emails each inbox may send per day", tag: "Limits set" },
      { text: "Send a test to our own inboxes and check it lands in the INBOX, not spam", tag: "Placement proved" },
      { stop: "Senior checks and signs off" },
    ],
    finishedWhen: "Domains warmed, limits set, and a test email proved to land in the inbox.",
  },
  G6: {
    title: "Build the list",
    before: [
      "Approved description of who we are targeting",
      "The client's do-not-contact list",
    ],
    steps: [
      { text: "Set the filters from the approved targeting, and save the search", tag: "Search saved" },
      { text: "Pull the list", tag: "Raw list" },
      { text: "Remove anyone already in the client's CRM", tag: "CRM deduped" },
      { text: "Remove anyone from earlier campaigns and from the LinkedIn list", tag: "Cross deduped" },
      { text: "Remove the client's own customers and anyone they are already talking to", tag: "Customers removed" },
      { text: "Apply the never-contact list: competitors, anyone who opted out before, general inboxes", tag: "Suppressed" },
      { text: "Verify the emails and drop the bad ones", tag: "Verified" },
      { text: "Split into groups that need different messages", tag: "Segments" },
      { stop: "Division Lead or named project lead sanity checks before anything is loaded" },
    ],
    finishedWhen: "List is clean, verified, suppressed, and split into segments.",
  },
  G7: {
    title: "Launch a cold email campaign",
    before: [
      "Domains warmed (card G5)",
      "List clean (card G6)",
      "Approved copy",
    ],
    steps: [
      { text: "Check the copy, the sending setup and the list all match this campaign", tag: "Matched" },
      { text: "Load the sequence exactly as approved", tag: "Loaded" },
      { text: "Set it to start slowly and build up over 1 to 2 weeks", tag: "Ramp set" },
      { text: "Send a test to OUR OWN inboxes first", tag: "Test sent" },
      { text: "Open the test and read it as a stranger: does the name fill in, do the links work, does it look right. NEVER launch a sequence no human has received and read.", tag: "Test read" },
      { stop: "Division Lead or named project lead approves the launch" },
      { text: "Launch, and tell the client it is live", tag: "Live" },
      { text: "Watch closely for the first 3 days: bounces, complaints, replies", tag: "Monitored" },
    ],
    finishedWhen: "Live, ramping slowly, and being watched daily for the first three days.",
  },
  G8: {
    title: "Replies — the part that matters most",
    before: [
      "Live campaigns",
      "You know where the client wants their leads sent",
    ],
    steps: [
      { text: "Check the inboxes at least TWICE every working day", tag: "Checked" },
      { text: "Sort every reply: interested, maybe later, referral, objection, not interested, stop emailing me", tag: "Sorted" },
      { text: "INTERESTED → move straight to booking a meeting", tag: "Meeting" },
      { text: "MAYBE LATER → agree a date, note it, take them off the sequence", tag: "Diarised" },
      { text: "REFERRAL → thank them, then add the new person as a WARM contact naming who referred them", tag: "Warm entry" },
      { text: "Never drop a referral into the normal cold sequence. Referrals convert far better", tag: "Handled right" },
      { text: "OBJECTION → answer once, using the approved answers. Do not argue twice", tag: "Answered" },
      { text: "STOP EMAILING ME → stop immediately and add to the permanent never-contact list for that client", tag: "Suppressed" },
      { text: "If you cannot answer accurately, send a holding reply and ask the Division Lead the SAME DAY", tag: "Escalated" },
      { text: "Send every interested person to the client within ONE WORKING DAY, with the whole conversation", tag: "Handed over" },
      { text: "Once a week, read back what prospects actually said and tell the Division Lead", tag: "Feedback" },
    ],
    finishedWhen: "No reply is older than one working day, and every interested person has reached the client.",
  },
  G9: {
    title: "Daily deliverability check",
    before: [
      "Live sending",
    ],
    steps: [
      { text: "Check the bounce rate", tag: "Bounce rate" },
      { text: "Above 3% → PAUSE and clean the list again", tag: "Paused" },
      { text: "Above 5% → STOP completely and check where the list came from", tag: "Stopped" },
      { text: "Check spam complaints. Above 0.1% → PAUSE and look at the copy and the targeting", tag: "Checked" },
      { text: "Check we are not on a blocklist. If we are → pause that domain and start getting it removed", tag: "Clean" },
      { text: "If opens drop a lot, check where the mail is landing before you carry on", tag: "Placement checked" },
      { text: "PAUSING NEVER NEEDS PERMISSION. Restarting does", tag: "Understood" },
    ],
    finishedWhen: "All thresholds checked. Anything breached is paused and the Division Lead knows.",
  },
  G10: {
    title: "Ebook, white paper or report",
    before: [
      "Approved positioning",
      "You know what happens after someone downloads it",
    ],
    steps: [
      { text: "Decide what happens AFTER the download. Without a follow-up sequence this is a file, not a campaign", tag: "Purpose clear" },
      { text: "Check people actually want this topic: search demand, sales objections, real questions", tag: "Validated" },
      { text: "Write the outline", tag: "Outline" },
      { stop: "Client approves the outline" },
      { text: "Gather research and sources", tag: "Research" },
      { text: "Write it", tag: "Draft" },
      { text: "Brief the design to the social team AT OUTLINE STAGE, not when the draft is done", tag: "Design raised" },
      { text: "Ask for the landing page and form, and confirm where the leads will go", tag: "Page raised" },
      { text: "Write the follow-up emails BEFORE launch", tag: "Sequence" },
      { stop: "Client approves the draft" },
      { text: "Launch, then track downloads AND what happened to those leads", tag: "Tracked" },
    ],
    finishedWhen: "Asset is live with a landing page, a follow-up sequence, and leads being tracked.",
  },
  G11: {
    title: "Webinar or online event",
    before: [
      "Topic and speaker agreed",
      "Speaker has confirmed the date",
    ],
    steps: [
      { text: "Confirm the speaker is actually available BEFORE announcing anything", tag: "Speaker locked" },
      { text: "Set the date with 3 to 4 weeks to promote it. Less than that does not work", tag: "Date set" },
      { text: "Ask the social team for the registration page and form", tag: "Page raised" },
      { text: "Test the registration form YOURSELF before any promotion starts. A broken form during promotion costs the whole event.", tag: "Form works" },
      { text: "Plan the promotion across email, LinkedIn, organic and outbound, with dates and names", tag: "Promo plan" },
      { text: "Write the reminders: on signup, one week, one day, one hour before. Without reminders, only about half the people who registered turn up.", tag: "Reminders" },
      { text: "Prepare the content and run through it once with the speaker", tag: "Rehearsed" },
      { text: "On the day, have someone OTHER than the speaker watching the chat", tag: "Chat covered" },
      { text: "Within 24 hours send the recording and resources", tag: "Follow-up" },
      { text: "Send DIFFERENT emails to people who attended and people who did not", tag: "Segmented" },
      { text: "Send the leads to the client and report registrations, attendance and leads", tag: "Reported" },
    ],
    finishedWhen: "Event delivered, follow-up sent within 24 hours, leads with the client, and reported.",
  },
  G12: {
    title: "Website build — we own it",
    before: [
      "Signed scope with the page count and the launch date",
      "One named person at the client who owns what the site says",
    ],
    steps: [
      { text: "List EVERY page the site will have, numbered. This list IS the scope. Anything not on that list later is a new request, and it is quoted.", tag: "Page list" },
      { text: "Name ONE person at the client who owns the words. Not a committee", tag: "Owner named" },
      { text: "Find out how many pages the OLD site has. Pull the full list now, not at launch", tag: "URL list" },
      { text: "List every file, form, video and account the site will need, with who supplies it and by when", tag: "Dependency list" },
      { text: "List every claim, figure, client name and logo, with where it came from", tag: "Proof list" },
      { text: "Write the copy for every page", tag: "Copy" },
      { stop: "Client approves the copy PAGE BY PAGE. A single site-wide yes means nobody read it. NOTHING IS BUILT BEFORE THIS. Building on changing copy means building twice." },
      { text: "Hand the page list and approved copy to the social team to build", tag: "Handed over" },
      { text: "Tell Tech and Automation what tracking and domains are needed, 5 working days ahead", tag: "Tech raised" },
      { text: "Check every page at desktop, laptop and phone. Report each one separately", tag: "Checked" },
      { text: "Check every claim against the proof list", tag: "Facts checked" },
      { text: "Check every link works, including from pages nobody thought to test", tag: "Links ok" },
      { text: "Map every OLD web address to a new one. Redirects go live IN THE SAME CHANGE as the switch. Any gap and every old link breaks.", tag: "Redirect map" },
      { text: "After launch, load the site fresh and confirm the tracking actually fires", tag: "Tracking live" },
      { text: "Check at day 7, day 30 and day 90", tag: "Reviews booked" },
    ],
    finishedWhen: "Site is live, redirects working, tracking confirmed firing, and the first week watched.",
  },
  G13: {
    title: "Monthly report",
    before: [
      "Full month of data",
      "The campaign and content logs",
    ],
    steps: [
      { text: "Pull the numbers for each channel: search, content, LinkedIn, outbound, email", tag: "Numbers" },
      { text: "Compare with the targets written in the strategy and with last month", tag: "Comparison" },
      { text: "Run the AI question set again, the SAME questions as last month", tag: "AI check" },
      { text: "What went well, and why you think so", tag: "Wins" },
      { text: "What did not, and what you are doing about it", tag: "Gaps" },
      { text: "What prospects actually SAID in replies. This is often the most useful page", tag: "Market feedback" },
      { text: "What we were waiting on from the client, and for how long. State it plainly", tag: "Dependencies" },
      { text: "What we delivered against what was planned", tag: "Delivered vs planned" },
      { text: "What you recommend for next month", tag: "Recommendations" },
      { stop: "Division Lead or named project lead checks it before it goes to Shreya. The founders review the master report (card O6)" },
      { text: "Send to Shreya between the 25th and 28th, for the master report", tag: "With Shreya" },
    ],
    finishedWhen: "Report with Shreya between the 25th and 28th, including what we were waiting on and what prospects said.",
  },
  F1: {
    title: "Client setup — do this BEFORE any work",
    before: [
      "A new client, or a client where something below is missing",
    ],
    steps: [
      { text: "Approved positioning exists: what they are, who for, why different. EVERYTHING we write comes from that. Without it, copy gets rewritten.", tag: "Positioning" },
      { text: "A word list exists: their terms, product names, short forms", tag: "Word list" },
      { text: "We know their numbers: what a customer is worth, how many leads become customers", tag: "Economics" },
      { text: "Proof exists: case studies, results, testimonials", tag: "Proof" },
      { text: "We know WHERE a lead goes when we get one", tag: "Lead destination" },
      { text: "We have access to everything: site, search console, analytics, LinkedIn, CRM", tag: "Access" },
      { text: "For outbound, we know which countries we are emailing into", tag: "Markets known" },
      { text: "ONE named person at the client approves our work, and we know the ONE channel where their approvals are written down", tag: "Approver named" },
      { text: "The client's dos and don'ts are written down: what we must never say, show or claim", tag: "Dos and don'ts" },
      { text: "Their tone of voice is written down. If it does not exist, writing it becomes a job", tag: "Tone of voice" },
      { text: "IF ANYTHING IS MISSING: tell the Division Lead. It becomes a job with a date", tag: "Raised" },
    ],
    finishedWhen: "All of it exists and is saved in the client's Foundation folder.",
  },
};
